import { useState, type ReactNode } from 'react';
import type { Editor } from '@tiptap/react';
import { useEditorState } from '@tiptap/react';
import {
  AlignCenter,
  AlignJustify,
  AlignLeft,
  AlignRight,
  Bold,
  Code,
  Heading1,
  Heading2,
  Heading3,
  Italic,
  Link2,
  Link2Off,
  List,
  ListOrdered,
  Minus,
  Palette,
  Quote,
  Redo2,
  Strikethrough,
  Text as TextIcon,
  Undo2,
  Underline,
} from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Toggle } from '@/components/ui/toggle';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import { sanitizeUrl } from '@/lib/identity';
import { cn } from '@/lib/utils';

interface EditorToolbarProps {
  editor: Editor | null;
  className?: string;
}

type BlockType = 'paragraph' | 'h1' | 'h2' | 'h3';
type Alignment = 'left' | 'center' | 'right' | 'justify';

const ALIGNMENT_ICONS = {
  left: AlignLeft,
  center: AlignCenter,
  right: AlignRight,
  justify: AlignJustify,
} as const;

interface ToolbarSnapshot {
  blockType: BlockType;
  alignment: Alignment;
  marks: {
    bold: boolean;
    italic: boolean;
    underline: boolean;
    strike: boolean;
    code: boolean;
  };
  active: {
    bulletList: boolean;
    orderedList: boolean;
    blockquote: boolean;
    codeBlock: boolean;
    link: boolean;
  };
  canUndo: boolean;
  canRedo: boolean;
}

const EMPTY_SNAPSHOT: ToolbarSnapshot = {
  blockType: 'paragraph',
  alignment: 'left',
  marks: { bold: false, italic: false, underline: false, strike: false, code: false },
  active: { bulletList: false, orderedList: false, blockquote: false, codeBlock: false, link: false },
  canUndo: false,
  canRedo: false,
};

export function EditorToolbar({ editor, className }: EditorToolbarProps) {
  const [linkDialogOpen, setLinkDialogOpen] = useState(false);
  const [linkUrl, setLinkUrl] = useState('');
  const [linkError, setLinkError] = useState<string | undefined>();

  const snapshot = useEditorState({
    editor,
    selector: ({ editor: ed }: { editor: Editor | null }): ToolbarSnapshot => {
      if (!ed) return EMPTY_SNAPSHOT;
      const blockType: BlockType = ed.isActive('heading', { level: 1 })
        ? 'h1'
        : ed.isActive('heading', { level: 2 })
          ? 'h2'
          : ed.isActive('heading', { level: 3 })
            ? 'h3'
            : 'paragraph';
      const alignment: Alignment = (['left', 'center', 'right', 'justify'] as const).find((a) =>
        ed.isActive({ textAlign: a }),
      ) ?? 'left';
      return {
        blockType,
        alignment,
        marks: {
          bold: ed.isActive('bold'),
          italic: ed.isActive('italic'),
          underline: ed.isActive('underline'),
          strike: ed.isActive('strike'),
          code: ed.isActive('code'),
        },
        active: {
          bulletList: ed.isActive('bulletList'),
          orderedList: ed.isActive('orderedList'),
          blockquote: ed.isActive('blockquote'),
          codeBlock: ed.isActive('codeBlock'),
          link: ed.isActive('link'),
        },
        canUndo: ed.can().undo(),
        canRedo: ed.can().redo(),
      };
    },
  });
  const state = snapshot ?? EMPTY_SNAPSHOT;

  if (!editor) {
    return <div className={cn('h-12 border-b bg-background/80', className)} aria-hidden />;
  }

  const setBlock = (type: BlockType) => {
    const chain = editor.chain().focus();
    if (type === 'paragraph') chain.setParagraph().run();
    else chain.setHeading({ level: Number(type[1]) as 1 | 2 | 3 }).run();
  };

  const setAlignment = (alignment: Alignment) => {
    editor.chain().focus().setTextAlign(alignment).run();
  };

  const openLinkDialog = () => {
    const existing = editor.getAttributes('link').href;
    setLinkUrl(existing ?? 'https://');
    setLinkError(undefined);
    setLinkDialogOpen(true);
  };

  const applyLink = () => {
    let candidate = linkUrl.trim();
    if (!candidate) {
      removeLink();
      return;
    }
    if (!/^[a-z][a-z0-9+.-]*:/i.test(candidate)) {
      candidate = `https://${candidate}`;
    }
    const safe = sanitizeUrl(candidate);
    if (!safe) {
      setLinkError('Only http(s) and mailto links are allowed.');
      return;
    }
    editor.chain().focus().extendMarkRange('link').setLink({ href: safe }).run();
    setLinkDialogOpen(false);
  };

  const removeLink = () => {
    editor.chain().focus().extendMarkRange('link').unsetLink().run();
    setLinkDialogOpen(false);
  };

  const AlignmentIcon = ALIGNMENT_ICONS[state.alignment];

  return (
    <div
      className={cn(
        'no-scrollbar flex h-12 items-center gap-0.5 overflow-x-auto border-b bg-background/95 px-2 backdrop-blur supports-[backdrop-filter]:bg-background/75',
        className,
      )}
      role="toolbar"
      aria-label="Formatting"
      onMouseDown={(event) => {
        // Keep the editor focused when tapping toolbar controls.
        if (event.target !== event.currentTarget && !(event.target as HTMLElement).closest('input')) {
          event.preventDefault();
        }
      }}
    >
      <IconButton label="Undo" disabled={!state.canUndo} onClick={() => editor.chain().focus().undo().run()}>
        <Undo2 className="h-4 w-4" />
      </IconButton>
      <IconButton label="Redo" disabled={!state.canRedo} onClick={() => editor.chain().focus().redo().run()}>
        <Redo2 className="h-4 w-4" />
      </IconButton>

      <ToolbarSeparator />

      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" className="h-9 gap-1.5 px-2.5 font-medium" aria-label="Text style">
            <BlockTypeIcon type={state.blockType} />
            <span className="hidden text-sm md:inline">{blockTypeLabel(state.blockType)}</span>
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" className="min-w-40">
          <BlockMenuItem type="paragraph" current={state.blockType} onSelect={setBlock} />
          <BlockMenuItem type="h1" current={state.blockType} onSelect={setBlock} />
          <BlockMenuItem type="h2" current={state.blockType} onSelect={setBlock} />
          <BlockMenuItem type="h3" current={state.blockType} onSelect={setBlock} />
        </DropdownMenuContent>
      </DropdownMenu>

      <ToolbarSeparator />

      <FormatToggle label="Bold" active={state.marks.bold} onClick={() => editor.chain().focus().toggleBold().run()}>
        <Bold className="h-4 w-4" />
      </FormatToggle>
      <FormatToggle label="Italic" active={state.marks.italic} onClick={() => editor.chain().focus().toggleItalic().run()}>
        <Italic className="h-4 w-4" />
      </FormatToggle>
      <FormatToggle label="Underline" active={state.marks.underline} onClick={() => editor.chain().focus().toggleUnderline().run()}>
        <Underline className="h-4 w-4" />
      </FormatToggle>
      <FormatToggle label="Strikethrough" active={state.marks.strike} onClick={() => editor.chain().focus().toggleStrike().run()}>
        <Strikethrough className="h-4 w-4" />
      </FormatToggle>
      <FormatToggle label="Inline code" active={state.marks.code} onClick={() => editor.chain().focus().toggleCode().run()}>
        <Code className="h-4 w-4" />
      </FormatToggle>

      <ToolbarSeparator />

      <FormatToggle label="Bulleted list" active={state.active.bulletList} onClick={() => editor.chain().focus().toggleBulletList().run()}>
        <List className="h-4 w-4" />
      </FormatToggle>
      <FormatToggle label="Numbered list" active={state.active.orderedList} onClick={() => editor.chain().focus().toggleOrderedList().run()}>
        <ListOrdered className="h-4 w-4" />
      </FormatToggle>

      <ToolbarSeparator />

      <FormatToggle label="Quote" active={state.active.blockquote} onClick={() => editor.chain().focus().toggleBlockquote().run()}>
        <Quote className="h-4 w-4" />
      </FormatToggle>
      <FormatToggle label="Code block" active={state.active.codeBlock} onClick={() => editor.chain().focus().toggleCodeBlock().run()}>
        <span className="font-mono text-[11px] font-bold">{'{ }'}</span>
      </FormatToggle>
      <IconButton label="Divider" onClick={() => editor.chain().focus().setHorizontalRule().run()}>
        <Minus className="h-4 w-4" />
      </IconButton>

      <ToolbarSeparator />

      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" size="icon" className="h-9 w-9 shrink-0" aria-label="Text alignment">
            <AlignmentIcon className="h-4 w-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start">
          {(['left', 'center', 'right', 'justify'] as const).map((alignment) => (
            <DropdownMenuItem key={alignment} onClick={() => setAlignment(alignment)} className="gap-2 capitalize">
              {(() => {
                const Icon = ALIGNMENT_ICONS[alignment];
                return <Icon className="h-4 w-4" />;
              })()}
              {alignment}
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>

      <FormatToggle label="Link" active={state.active.link} onClick={openLinkDialog}>
        <Link2 className="h-4 w-4" />
      </FormatToggle>
      {state.active.link && (
        <IconButton label="Remove link" onClick={removeLink}>
          <Link2Off className="h-4 w-4" />
        </IconButton>
      )}
      <IconButton label="Clear formatting" onClick={() => editor.chain().focus().unsetAllMarks().clearNodes().run()}>
        <Palette className="h-4 w-4" />
      </IconButton>

      <Dialog open={linkDialogOpen} onOpenChange={setLinkDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Insert link</DialogTitle>
            <DialogDescription>
              Attach an https:// or mailto: URL to the selected text.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label htmlFor="doc-link-url">URL</Label>
            <Input
              id="doc-link-url"
              value={linkUrl}
              onChange={(event_) => {
                setLinkUrl(event_.target.value);
                setLinkError(undefined);
              }}
              placeholder="https://example.com"
              inputMode="url"
              autoComplete="off"
              onKeyDown={(event_) => {
                if (event_.key === 'Enter') {
                  event_.preventDefault();
                  applyLink();
                }
              }}
              aria-invalid={linkError ? true : undefined}
            />
            {linkError && <p className="text-sm text-destructive">{linkError}</p>}
          </div>
          <DialogFooter className="flex-row gap-2 sm:justify-between">
            {state.active.link ? (
              <Button type="button" variant="ghost" onClick={removeLink}>
                Remove
              </Button>
            ) : <span />}
            <Button type="button" onClick={applyLink}>Apply</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function blockTypeLabel(type: BlockType): string {
  switch (type) {
    case 'h1': return 'Heading 1';
    case 'h2': return 'Heading 2';
    case 'h3': return 'Heading 3';
    default: return 'Text';
  }
}

function BlockTypeIcon({ type }: { type: BlockType }) {
  switch (type) {
    case 'h1': return <Heading1 className="h-4 w-4" />;
    case 'h2': return <Heading2 className="h-4 w-4" />;
    case 'h3': return <Heading3 className="h-4 w-4" />;
    default: return <TextIcon className="h-4 w-4" />;
  }
}

function BlockMenuItem({
  type,
  current,
  onSelect,
}: {
  type: BlockType;
  current: BlockType;
  onSelect: (type: BlockType) => void;
}) {
  return (
    <DropdownMenuItem
      onClick={() => onSelect(type)}
      className={cn('gap-2', current === type && 'bg-accent font-medium')}
    >
      <BlockTypeIcon type={type} />
      {blockTypeLabel(type)}
    </DropdownMenuItem>
  );
}

function IconButton({
  label,
  onClick,
  disabled,
  children,
}: {
  label: string;
  onClick: () => void;
  disabled?: boolean;
  children: React.ReactNode;
}) {
  return (
    <Button
      type="button"
      variant="ghost"
      size="icon"
      className="h-9 w-9 shrink-0"
      onClick={onClick}
      disabled={disabled}
      aria-label={label}
      title={label}
    >
      {children}
    </Button>
  );
}

function FormatToggle({
  label,
  active,
  onClick,
  children,
}: {
  label: string;
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <Toggle
      size="default"
      pressed={active}
      onPressedChange={() => onClick()}
      className="h-9 w-9 shrink-0 p-0"
      aria-label={label}
      title={label}
    >
      {children}
    </Toggle>
  );
}

function ToolbarSeparator() {
  return <Separator orientation="vertical" className="mx-1 h-6 shrink-0" />;
}
